import React from "react";

import EditorIcon from "@uui/Edit02";
import ViewerIcon from "@uui/Eye";
import LockIcon from "@uui/Lock03";

import type { SelectChangeEvent, SvgIconProps } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import Setting from "@app/analytics/account/components/Setting";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingWithToggle from "@app/analytics/account/components/SettingWithToggle";
import { Divider } from "@app/analytics/account/components/styles";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { useAuth } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { ParticipantAccessLevel, UserSettings } from "@app/common/types";
import * as UserUtils from "@app/common/utils/userUtils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import { RecommendedBadge } from "@rui/Badge";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { DropdownItem } from "@rui/Dropdown/types";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import IconBackground from "@rui/IconBackground";
import { Column, Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import InternalAccessUnavailable from "./InternalAccessUnavailable";

const ICON_FOR_ACCESS_LEVEL: {
  [key in ParticipantAccessLevel]: typeof EditorIcon;
} = {
  editor: EditorIcon,
  viewer_full: ViewerIcon,
  no_access: LockIcon,
};

const ACCESS_OPTIONS: DropdownItem<ParticipantAccessLevel>[] = [
  { value: "editor", label: "Editor access" },
  { value: "viewer_full", label: "Viewer access" },
];

const DropdownIcon = ({
  Icon,
}: {
  Icon: React.FunctionComponent<SvgIconProps>;
}) => (
  <IconBackground height={20} bgcolor="shades.ghost.500">
    <Icon sx={{ color: "shades.primary.500" }} />
  </IconBackground>
);

const AccessDropdown = ({
  disabled,
  name,
  onChange,
  recommended,
  selected,
  tooltip,
}: {
  disabled: boolean;
  name: string;
  onChange: (event: SelectChangeEvent<ParticipantAccessLevel>) => void;
  recommended: ParticipantAccessLevel;
  selected?: ParticipantAccessLevel;
  tooltip?: string;
}) => {
  const selectedItem = selected
    ? getSelectedFromOptions<ParticipantAccessLevel>(ACCESS_OPTIONS, selected)
    : undefined;

  return (
    <TooltipWrapper placement="right" show={disabled} title={tooltip}>
      <Dropdown
        name={name}
        disabled={disabled}
        onChange={onChange}
        selectedItem={selectedItem}
        renderSelectedItem={(item) => (
          <Row variant="left" spacing={1}>
            <DropdownIcon Icon={ICON_FOR_ACCESS_LEVEL[item.value]} />
            <Typography variant="bodySecondaryReg" color="shades.neutral.800">
              {item.label}
            </Typography>
          </Row>
        )}
        wrapperSx={{ width: "100%", maxWidth: "300px" }}
      >
        {ACCESS_OPTIONS.map(({ value, label }) => (
          <DropdownMenuItem
            key={value}
            value={value}
            label={label}
            startIcon={<DropdownIcon Icon={ICON_FOR_ACCESS_LEVEL[value]} />}
            isDefault={value === recommended}
            renderDefaultLabel={() => <RecommendedBadge />}
          />
        ))}
      </Dropdown>
    </TooltipWrapper>
  );
};

const ParticipantAccessSettings = () => {
  const { user } = useAuth();
  const { inWorkspace } = useWorkspace();
  const { userSettings, updateUserSettings } = useUserSettings();
  const {
    canEditInternalParticipantAccessLevel,
    canEditExternalParticipantAccessLevel,
  } = useFeatureAccess();

  const isCompanyEmail = UserUtils.hasCompanyEmail(user);
  const userDomain = UserUtils.getEmailDomain(user);

  const handleToggleInternalAccessEnabled = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateUserSettings.mutate({
      internalParticipantAccessLevel: checked ? "editor" : "no_access",
    });
  };

  const handleToggleExternalAccessEnabled = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateUserSettings.mutate({
      externalParticipantAccessLevel: checked ? "viewer_full" : "no_access",
    });
  };

  const handlePreferenceChange =
    (preferenceName: keyof UserSettings) =>
    (event: SelectChangeEvent<ParticipantAccessLevel>) => {
      updateUserSettings.mutate({
        [preferenceName]: event.target.value,
      });
    };

  const internalParticipantAccessLevelDisabledProps = getSettingDisabledOpts(
    canEditInternalParticipantAccessLevel && isCompanyEmail
  );
  const externalParticipantAccessLevelDisabledProps = getSettingDisabledOpts(
    canEditExternalParticipantAccessLevel
  );

  return (
    <>
      <Setting title="Participant Access">
        <SettingContent spacing={1.5}>
          <Column>
            <SettingWithToggle
              title="Internal Participant Access"
              description={`Automatically grant report access for internal ${
                isCompanyEmail ? `(${userDomain}) ` : ""
              }meeting participants`}
              checked={
                userSettings.internalParticipantAccessLevel !== "no_access"
              }
              disabled={internalParticipantAccessLevelDisabledProps.disabled}
              disabledTooltip={
                !isCompanyEmail || !inWorkspace
                  ? undefined
                  : internalParticipantAccessLevelDisabledProps.tooltip
              }
              onChange={handleToggleInternalAccessEnabled}
            />
            {!isCompanyEmail && (
              <InternalAccessUnavailable userDomain={userDomain} />
            )}
            <Collapse
              in={
                isCompanyEmail &&
                userSettings.internalParticipantAccessLevel !== "no_access"
              }
              sx={{ width: "100%" }}
            >
              <AccessDropdown
                name="internal-participant-access-selector"
                disabled={internalParticipantAccessLevelDisabledProps.disabled}
                recommended="editor"
                selected={userSettings.internalParticipantAccessLevel}
                onChange={handlePreferenceChange(
                  "internalParticipantAccessLevel"
                )}
                tooltip={internalParticipantAccessLevelDisabledProps.tooltip}
              />
            </Collapse>
          </Column>
          <Divider />
          <Column>
            <SettingWithToggle
              title="External Participant Access"
              description={`Automatically grant report access for external ${
                isCompanyEmail ? `(not ${userDomain}) ` : ""
              }meeting participants`}
              checked={
                userSettings.externalParticipantAccessLevel !== "no_access"
              }
              disabled={externalParticipantAccessLevelDisabledProps.disabled}
              disabledTooltip={
                externalParticipantAccessLevelDisabledProps.tooltip
              }
              onChange={handleToggleExternalAccessEnabled}
            />
            <Collapse
              in={userSettings.externalParticipantAccessLevel !== "no_access"}
            >
              <AccessDropdown
                name="external-participant-access-selector"
                disabled={externalParticipantAccessLevelDisabledProps.disabled}
                recommended="viewer_full"
                selected={userSettings.externalParticipantAccessLevel}
                onChange={handlePreferenceChange(
                  "externalParticipantAccessLevel"
                )}
                tooltip={externalParticipantAccessLevelDisabledProps.tooltip}
              />
            </Collapse>
          </Column>
        </SettingContent>
      </Setting>
      <Divider />
    </>
  );
};

export default ParticipantAccessSettings;
